'use client';
import http from '@/utils/httpClient';
import { toaster } from '@/utils/toast';
import { Action, Thunk, action, thunk } from 'easy-peasy';

export interface TosVersion {
  id: string;
  version: string;
  effectiveAt: string;
  createdAt: string;
}

export interface TosTriggeredBy {
  id: string;
  firstName: string;
  lastName: string;
  emailId: string;
}

export interface TosSendJob {
  id: string;
  versionId: string;
  triggeredByUserId: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  dryRun: boolean;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  tos_version?: TosVersion;
  triggeredBy?: TosTriggeredBy;
}

export interface TosJobsResponse {
  jobs: TosSendJob[];
  totalCount: number;
  page: number;
  perPage: number;
}

export interface CreateSendJobPayload {
  versionId: string;
  dryRun: boolean;
}

export interface ITosModel {
  versions: TosVersion[] | undefined;
  versionsLoading: boolean;
  jobs: TosJobsResponse | undefined;
  jobsLoading: boolean;
  sending: boolean;

  /*-------- ACTION TYPE ----------*/
  setVersions: Action<ITosModel, TosVersion[]>;
  setVersionsLoading: Action<ITosModel, boolean>;
  setJobs: Action<ITosModel, TosJobsResponse>;
  setJobsLoading: Action<ITosModel, boolean>;
  setSending: Action<ITosModel, boolean>;

  /*-------- THUNK TYPE -----------*/
  fetchVersions: Thunk<ITosModel>;
  fetchJobs: Thunk<ITosModel, { page?: number; perPage?: number } | undefined>;
  sendJob: Thunk<ITosModel, CreateSendJobPayload, undefined, object, Promise<boolean>>;
}

const TosModel: ITosModel = {
  versions: undefined,
  versionsLoading: false,
  jobs: undefined,
  jobsLoading: false,
  sending: false,

  /*------- ACTION TYPE -------*/
  setVersions: action((state, value) => {
    state.versions = value;
  }),
  setVersionsLoading: action((state, value) => {
    state.versionsLoading = value;
  }),
  setJobs: action((state, value) => {
    state.jobs = value;
  }),
  setJobsLoading: action((state, value) => {
    state.jobsLoading = value;
  }),
  setSending: action((state, value) => {
    state.sending = value;
  }),

  /*------- THUNK TYPE --------*/
  fetchVersions: thunk(async (actions) => {
    actions.setVersionsLoading(true);
    try {
      const res = await http().get('/admin/tos/versions');
      actions.setVersions(res.data?.versions || []);
    } catch {
      toaster('error', 'Could not load ToS versions');
    } finally {
      actions.setVersionsLoading(false);
    }
  }),

  fetchJobs: thunk(async (actions, payload) => {
    actions.setJobsLoading(true);
    try {
      const page = payload?.page ?? 1;
      const perPage = payload?.perPage ?? 10;
      const res = await http().get(`/admin/tos/jobs?page=${page}&perPage=${perPage}`);
      actions.setJobs(res.data);
    } catch {
      toaster('error', 'Could not load ToS send history');
    } finally {
      actions.setJobsLoading(false);
    }
  }),

  sendJob: thunk(async (actions, payload) => {
    actions.setSending(true);
    try {
      await http().post('/admin/tos/send', payload);
      toaster(
        'success',
        payload.dryRun
          ? 'Dry-run send queued (you should receive an email shortly).'
          : 'Send job created. The worker will drip out emails over the coming hours/days.'
      );
      await actions.fetchJobs(undefined);
      return true;
    } catch (e: any) {
      // 429 from rate limiter -> user-friendly message.
      const status = e?.response?.status;
      const message =
        e?.response?.data?.error ||
        (status === 429
          ? 'Too many send requests. Please wait a minute and try again.'
          : 'Failed to create ToS send job');
      toaster('error', message);
      return false;
    } finally {
      actions.setSending(false);
    }
  }),
};

export default TosModel;
